<template>
    <b-modal
        id="add-to-medsync-modal"
        ref="modal"
        title="Add Medication to MedSync"
        @ok="handleOk"
        @hidden="onHidden">
        <div v-if="loading" class="text-center py-3">
            <b-spinner label="Loading..."></b-spinner>
        </div>

        <div v-else-if="enrollment && prescription">
            <b-alert variant="info" show>
                <i class="fa fa-info-circle"></i>
                You are about to add this medication to the patient's MedSync program.
            </b-alert>

            <!-- Prescription Details -->
            <h6 class="font-weight-bold mb-2">Medication</h6>
            <b-card class="mb-3">
                <div><strong>Rx ID:</strong> {{ prescription.rxID }}</div>
                <div><strong>Drug:</strong> {{ prescription.drugName }}</div>
                <div v-if="prescription.daySupply">
                    <strong>Days Supply:</strong> {{ prescription.daySupply }} days
                </div>
            </b-card>

            <!-- MedSync Program Details -->
            <h6 class="font-weight-bold mb-2">MedSync Program</h6>
            <b-card class="mb-3">
                <div>
                    <strong>Next Anchor Date:</strong> <span class="text-primary">{{ formatDate(enrollment.anchorDate) }}</span>
                </div>
                <div><strong>Cycle:</strong> {{ enrollment.cycleDays }} days</div>
                <div>
                    <strong>Current Medications:</strong> {{ medicationCount }}
                </div>
            </b-card>

            <!-- Alignment Information -->
            <h6 class="font-weight-bold mb-2">Fill Date Alignment</h6>
            <b-card class="mb-3" :border-variant="alignmentVariant">
                <div v-if="needsShortFill">
                    <div class="mb-2">
                        <i class="fa fa-exclamation-triangle text-warning"></i>
                        <strong>Short Fill Recommended</strong>
                    </div>
                    <p class="mb-2">
                        To synchronize this medication with the anchor date, a short fill may be needed
                        on the next refill.
                    </p>
                    <div class="text-muted">
                        <small>
                            The system will calculate the appropriate days supply adjustment
                            when processing the next refill to align with
                            <strong>{{ formatDate(enrollment.anchorDate) }}</strong>.
                        </small>
                    </div>
                </div>
                <div v-else>
                    <i class="fa fa-check-circle text-success"></i>
                    This medication can be synchronized with the existing anchor date.
                </div>
            </b-card>

            <!-- Autorefill Subscription Notice -->
            <b-alert variant="warning" show class="mb-0">
                <small>
                    <strong>Note:</strong> Adding this medication to MedSync will create an autorefill
                    subscription. If an existing autorefill subscription exists, it will be cancelled
                    and replaced with a MedSync-aligned subscription.
                </small>
            </b-alert>
        </div>

        <div v-else class="text-center text-muted py-4">
            <i class="fa fa-exclamation-circle fa-2x mb-2"></i>
            <p>Unable to load prescription or MedSync enrollment details.</p>
        </div>

        <template #modal-footer="{ ok, cancel }">
            <b-button variant="secondary" @click="cancel()">
                Cancel
            </b-button>
            <b-button variant="primary" @click="ok()" :disabled="saving || !canAdd">
                <b-spinner v-if="saving" small class="mr-1"></b-spinner>
                {{ saving ? 'Adding...' : 'Add to MedSync' }}
            </b-button>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import axios from 'axios';
import moment from 'moment';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { MedSyncEnrollment } from "@/models/MedSync";

interface PrescriptionInfo {
    rxID: string;
    storeID: number | null;
    rxNumber: number | null;
    rfNumber: number | null;
    drugName: string;
    daySupply: number | null;
    nextFillDate: Date | string | null;
}

@Component
export default class AddToMedSyncModal extends Vue {
    @Prop({ required: true }) patientId!: number;
    @Prop({ default: null }) rxId!: string | null;

    private enrollment: MedSyncEnrollment | null = null;
    private prescription: PrescriptionInfo | null = null;
    private medicationCount = 0;
    private loading = false;
    private saving = false;

    async show(rxId?: string) {
        if (rxId) {
            this.rxId = rxId;
        }
        (this.$refs.modal as any).show();
        await this.loadData();
    }

    close() {
        (this.$refs.modal as any).hide();
    }

    onHidden() {
        this.enrollment = null;
        this.prescription = null;
        this.medicationCount = 0;
    }

    async loadData() {
        if (!this.rxId) return;

        this.loading = true;
        try {
            // Load enrollment
            const enrollmentResponse = await axios.get(`/api/medsync/enrollment/${this.patientId}`);
            if (enrollmentResponse.data.enrolled) {
                this.enrollment = enrollmentResponse.data.enrollment;
                this.medicationCount = enrollmentResponse.data.prescriptions?.length || 0;
            }

            // Load prescription details from eligible prescriptions
            const eligibleResponse = await axios.get(`/api/medsync/eligible-prescriptions/${this.patientId}`);
            const eligiblePrescriptions = eligibleResponse.data;
            this.prescription = eligiblePrescriptions.find((p: PrescriptionInfo) => p.rxID === this.rxId) || null;
        } catch (error) {
            console.error('Error loading add to MedSync data:', error);
            this.$notification(NotificationOptions.error('Failed to load prescription or enrollment details'));
        } finally {
            this.loading = false;
        }
    }

    async handleOk(bvModalEvt: any) {
        bvModalEvt.preventDefault();
        await this.addToMedSync();
    }

    async addToMedSync() {
        if (!this.enrollment || !this.rxId) return;

        this.saving = true;
        try {
            // Add prescription to existing MedSync enrollment
            await axios.post(`/api/medsync/enrollment/${this.enrollment.id}/add-prescription`, {
                rxId: this.rxId,
            });

            this.$notification(
                NotificationOptions.success(`Successfully added medication to MedSync program`)
            );

            this.$emit('added', { rxId: this.rxId, enrollmentId: this.enrollment.id });
            this.close();
        } catch (error: any) {
            console.error('Error adding to MedSync:', error);
            const message = error.response?.data?.message || 'Failed to add medication to MedSync';
            this.$notification(NotificationOptions.error(message));
        } finally {
            this.saving = false;
        }
    }

    get canAdd(): boolean {
        return !!(this.enrollment && this.prescription && !this.loading);
    }

    get needsShortFill(): boolean {
        if (!this.enrollment?.anchorDate || !this.prescription?.nextFillDate) {
            return false;
        }

        const anchorDate = moment(this.enrollment.anchorDate);
        const nextFill = moment(this.prescription.nextFillDate);

        // If next fill is before anchor date, may need short fill
        return nextFill.isBefore(anchorDate, 'day');
    }

    get alignmentVariant(): string {
        return this.needsShortFill ? 'warning' : 'success';
    }

    formatDate(date: Date | string | null): string {
        if (!date) return 'N/A';
        return moment(date).format('MMMM D, YYYY');
    }
}
</script>

<style scoped>
.card {
    background-color: #f8f9fa;
}
</style>
